import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../../contexts/LanguageContext';
import { SalonProvider } from '../../contexts/SalonContext';
import Navigation from '../common/Navigation';
import ManageBrands from './ManageBrands';
import ManageDresses from './ManageDresses';
import ManageReservations from './ManageReservations';
import ManageAccessories from './ManageAccessories';
import ManageAppointments from './ManageAppointments';
import ManageTimeOff from './ManageTimeOff';
import ManageSettings from './ManageSettings';
import ManageAnalytics from './ManageAnalytics';
import useScrollLock from '../../hooks/useScrollLock';
import './Salon.css';

const SalonDashboard = () => {
  const { t } = useLanguage();
  const { salonId, section, page } = useParams();
  const navigate = useNavigate();
  const [salon, setSalon] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  // Get current view from URL or default to 'appointments'
  const currentView = section || 'appointments';
  const currentPage = page ? parseInt(page, 10) : 1;

  // Lock body scroll when mobile menu is open
  useScrollLock(isMobileMenuOpen);

  useEffect(() => {
    const fetchSalonDetails = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`/api/salons/${salonId}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        setSalon(response.data.salon);
        setError('');
      } catch (error) {
        if (error.response?.data?.code) {
          // Handle subscription-specific errors
          const code = error.response.data.code;
          if (code === 'TRIAL_EXPIRED' || code === 'SUBSCRIPTION_EXPIRED' || code === 'SUBSCRIPTION_INACTIVE') {
            setError('subscription_expired');
          } else {
            setError(error.response.data.error || 'Failed to fetch salon details');
          }
        } else if (error.response?.data?.error) {
          setError(error.response.data.error);
        } else {
          setError('Failed to fetch salon details');
        }
      } finally {
        setLoading(false);
      }
    };

    if (salonId) {
      fetchSalonDetails();
    }
  }, [salonId]);

  const handleMobileMenuToggle = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };


  const navigateToSection = (sectionName, pageNum = 1) => {
    setIsMobileMenuOpen(false);
    if (pageNum === 1) {
      navigate(`/salon/${salonId}/${sectionName}`);
    } else {
      navigate(`/salon/${salonId}/${sectionName}/${pageNum}`);
    }
  };

  // Time & PTO doesn't paginate its primary list via the URL, so the same :page route
  // slot is repurposed to hold the active tab name instead of a page number. 'myPto' is the
  // default tab, so it's the one that gets the bare (no-suffix) URL.
  const handleTimeOffTabChange = (tabName, { replace = false } = {}) => {
    const path = tabName === 'myPto' ? `/salon/${salonId}/time-off` : `/salon/${salonId}/time-off/${tabName}`;
    navigate(path, replace ? { replace: true } : undefined);
  };

  const handleManageBrandsClick = () => {
    navigateToSection('brands');
  };

  const handleManageDressesClick = () => {
    navigateToSection('dresses');
  };

  const handleManageReservationsClick = () => {
    navigateToSection('reservations');
  };

  const handleManageAccessoriesClick = () => {
    navigateToSection('accessories');
  };

  const handleManageAppointmentsClick = () => {
    navigateToSection('appointments');
  };

  const handleManageTimeOffClick = () => {
    navigateToSection('time-off');
  };

  const handleManageSettingsClick = () => {
    if (salon.userRole === 'member') {
      return; // Members cannot access settings
    }
    navigateToSection('settings');
  };

  const handleManageAnalyticsClick = () => {
    if (salon.userRole === 'member') {
      return; // Members cannot access analytics
    }
    navigateToSection('analytics');
  };

  const handleNavigateToReservationsSimple = () => {
    navigateToSection('reservations');
  };

  // Function to handle page changes from child components
  const handlePageChange = (newPage) => {
    navigateToSection(currentView, newPage);
  };


  if (loading) {
    return (
      <div className="salon-dashboard-container">
        <div className="dashboard-loading-message">
          <div className="dashboard-loading-spinner"></div>
          {t('common.loading')}...
        </div>
      </div>
    );
  }

  if (error) {
    if (error === 'subscription_expired') {
      return (
        <div className="salon-dashboard-container">
          <div className="dashboard-error-message subscription-expired">
            <div className="error-icon">⏰</div>
            <h2>{t('subscription.trialExpired')}</h2>
            <p>{t('subscription.trialExpiredMessage')}</p>
            <div className="error-actions">
              <button onClick={() => navigate('/pricing')} className="salon-button primary">
                {t('subscription.viewPlans')}
              </button>
              <button onClick={() => navigate('/dashboard')} className="salon-button secondary">
                {t('navigation.backToDashboard')}
              </button>
            </div>
          </div>
        </div>
      );
    }
    
    return (
      <div className="salon-dashboard-container">
        <div className="dashboard-error-message">
          {error}
          <button onClick={() => navigate('/dashboard')} className="salon-button secondary">
            {t('navigation.backToDashboard')}
          </button>
        </div>
      </div>
    );
  }

  if (!salon) {
    return (
      <div className="salon-dashboard-container">
        <div className="dashboard-error-message">
          {t('messages.error.notFound')}
          <button onClick={() => navigate('/dashboard')} className="salon-button secondary">
            {t('navigation.backToDashboard')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <SalonProvider salonId={salonId}>
      <div className="salon-dashboard-container">
        <Navigation 
          title={salon.name} 
          onMobileMenuToggle={handleMobileMenuToggle}
        />
        <div className="salon-dashboard-layout">
        {/* Left Side Panel */}
        {/* Mobile Menu Backdrop */}
        {isMobileMenuOpen && (
          <div className="mobile-menu-backdrop" onClick={handleMobileMenuToggle}></div>
        )}
        
        <div className={`salon-side-panel ${isMobileMenuOpen ? 'open' : ''}`}>
          <div className="side-panel-header">
            <h3>{t('common.actions')}</h3>
          </div>
          <div className="side-panel-actions">
            <button 
              className={`side-panel-action secondary ${currentView === 'appointments' ? 'active' : ''}`}
              onClick={handleManageAppointmentsClick}
            >
              <span className="action-icon">📅</span>
              {t('navigation.appointments')}
            </button>
            <button 
              className={`side-panel-action secondary ${currentView === 'reservations' ? 'active' : ''}`}
              onClick={handleManageReservationsClick}
            >
              <span className="action-icon">📋</span>
              {t('navigation.reservations')}
            </button>
            <button 
              className={`side-panel-action secondary ${currentView === 'accessories' ? 'active' : ''}`}
              onClick={handleManageAccessoriesClick}
            >
              <span className="action-icon">📦</span>
              {t('navigation.accessories')}
            </button>
            <button 
              className={`side-panel-action secondary ${currentView === 'dresses' ? 'active' : ''}`}
              onClick={handleManageDressesClick}
            >
              <span className="action-icon">👗</span>
              {t('navigation.dresses')}
            </button>
            <button
              className={`side-panel-action secondary ${currentView === 'brands' ? 'active' : ''}`}
              onClick={handleManageBrandsClick}
            >
              <span className="action-icon">🏷️</span>
              {t('navigation.brands')}
            </button>
            <button
              className={`side-panel-action secondary ${currentView === 'time-off' ? 'active' : ''}`}
              onClick={handleManageTimeOffClick}
            >
              <span className="action-icon">🕒</span>
              {t('navigation.timeOff')}
            </button>
            {salon.userRole === 'owner' || salon.userRole === 'admin' ? (
              <>
                <button 
                  className={`side-panel-action secondary ${currentView === 'analytics' ? 'active' : ''}`}
                  onClick={handleManageAnalyticsClick}
                >
                   <span className="action-icon">📊</span>
                   {t('navigation.analytics')}
                </button>
                <button 
                  className={`side-panel-action secondary ${currentView === 'settings' ? 'active' : ''}`}
                  onClick={handleManageSettingsClick}
                >
                  <span className="action-icon">⚙️</span>
                  {t('navigation.settings')}
                </button>
              </>
            ) : null}
          </div>
          
          {/* Salon Information Section */}
          <div className="side-panel-info">
            <div className="side-panel-info-header">
              <h3>{t('salon.salonInformation')}</h3>
            </div>
            <div className="side-panel-info-content">
              <table className="info-table">
                <tbody>
                  <tr>
                    <td className="info-label">{t('dashboard.created')}:</td>
                    <td className="info-value">{new Date(salon.created_at).toLocaleDateString()}</td>
                  </tr>
                  <tr>
                    <td className="info-label">{t('salon.role')}:</td>
                    <td className="info-value role-text">{salon.userRole || t('common.loading')}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="salon-main-content">
          {currentView === 'brands' ? (
            <div className="manage-workspace">
              <ManageBrands 
                salonId={salonId} 
                userRole={salon.userRole}
                currentPage={currentPage}
                onPageChange={handlePageChange}
              />
            </div>
          ) : currentView === 'dresses' ? (
            <div className="manage-workspace">
              <ManageDresses 
                salonId={salonId} 
                userRole={salon.userRole}
                salon={salon}
                currentPage={currentPage}
                onPageChange={handlePageChange}
              />
            </div>
          ) : currentView === 'reservations' ? (
            <div className="manage-workspace">
              <ManageReservations
                salonId={salonId} 
                userRole={salon.userRole}
                currentPage={currentPage}
                onPageChange={handlePageChange}
              />
            </div>
          ) : currentView === 'accessories' ? (
            <div className="manage-workspace">
              <ManageAccessories
                salonId={salonId} 
                userToken={localStorage.getItem('token')}
                userRole={salon.userRole}
                currentPage={currentPage}
                onPageChange={handlePageChange}
              />
            </div>
          ) : currentView === 'appointments' ? (
            <div className="manage-workspace">
              <ManageAppointments
                salonId={salonId}
                userRole={salon.userRole}
                onNavigateToReservations={handleNavigateToReservationsSimple}
                currentPage={currentPage}
                onPageChange={handlePageChange}
              />
            </div>
          ) : currentView === 'time-off' ? (
            <div className="manage-workspace">
              <ManageTimeOff
                salonId={salonId}
                userRole={salon.userRole}
                activeTab={page || 'myPto'}
                onTabChange={handleTimeOffTabChange}
              />
            </div>
          ) : currentView === 'settings' ? (
            salon.userRole === 'member' ? (
              <div className="manage-workspace">
                <div className="error-message">
                  {t('messages.error.unauthorized')}
                </div>
              </div>
            ) : (
              <div className="manage-workspace">
                <ManageSettings 
                  salonId={salonId} 
                  userRole={salon.userRole}
                  salon={salon}
                  onSalonUpdate={() => {
                    const token = localStorage.getItem('token');
                    axios.get(`/api/salons/${salonId}`, { headers: { Authorization: `Bearer ${token}` } })
                      .then((r) => setSalon(r.data.salon))
                      .catch(() => {});
                  }}
                />
              </div>
            )
          ) : currentView === 'analytics' ? (
            salon.userRole === 'member' ? (
              <div className="manage-workspace">
                <div className="error-message">
                  {t('messages.error.unauthorized')}
                </div>
              </div>
            ) : (
              <div className="manage-workspace">
                <ManageAnalytics 
                  salonId={salonId} 
                  userRole={salon.userRole}
                />
              </div>
            )
          ) : (
            <div className="manage-workspace">
              <ManageAppointments 
                salonId={salonId} 
                userRole={salon.userRole}
                onNavigateToReservations={handleNavigateToReservationsSimple}
                currentPage={currentPage}
                onPageChange={handlePageChange}
              />
            </div>
          )}
        </div>
      </div>
    </div>
    </SalonProvider>
  );
};

export default SalonDashboard;
